import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabaseClient, supabaseCoreClient } from '../../utils/supabase';
import { ACTION_DIALOG_REGISTRY } from './registry';
import { StripeModeBadge } from './StripeModeBadge';
import type { DetailAction } from './types';

interface DetailActionBarProps {
  actions: DetailAction[];
  record: Record<string, unknown>;
  tableName: string;
}

function hasAchContext(actions: DetailAction[], tableName: string): boolean {
  if (tableName.startsWith('ach_')) return true;
  return actions.some((a) => a.permission?.table?.startsWith('ach_'));
}

/** "/ach-charge?merchant=$record.id" -> "/ach-charge?merchant=123" */
function interpolatePath(template: string, record: Record<string, unknown> | undefined): string {
  return template.replace(/\$record\.([a-zA-Z0-9_]+)/g, (_m, field: string) =>
    encodeURIComponent(String(record?.[field] ?? '')));
}

function interpolatePrefill(
  prefill: Record<string, string> | undefined,
  record: Record<string, unknown>,
): Record<string, unknown> {
  if (!prefill) return {};
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(prefill)) {
    if (typeof value === 'string' && value.startsWith('$record.')) {
      const field = value.slice('$record.'.length);
      result[key] = record[field];
    } else {
      result[key] = value;
    }
  }
  return result;
}

export const DetailActionBar: React.FC<DetailActionBarProps> = ({ actions, record, tableName }) => {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState<string | null>(null);

  const permissionChecks = useMemo(
    () =>
      actions
        .filter((a) => a.permission)
        .map((a) => ({ key: a.key, table: a.permission!.table, action: a.permission!.action })),
    [actions],
  );

  const { data: permissions, isLoading: permissionsLoading } = useQuery({
    queryKey: ['action_permissions', ...permissionChecks.map((c) => `${c.table}:${c.action}`)],
    queryFn: async () => {
      const results: Record<string, boolean> = {};
      for (const check of permissionChecks) {
        const { data } = await supabaseCoreClient
          .rpc('has_table_permission', { p_table_name: check.table, p_permission: check.action });
        results[check.key] = !!data;
      }
      return results;
    },
    staleTime: 60_000,
    enabled: permissionChecks.length > 0,
  });

  const visibleWhenFields = useMemo(
    () => [...new Set(actions.filter((a) => a.visible_when).map((a) => a.visible_when!.field))],
    [actions],
  );

  const recordId = typeof record.id === 'number' ? record.id : null;
  const { data: actionVisibility } = useQuery({
    queryKey: ['action-visibility', tableName, recordId, ...visibleWhenFields],
    queryFn: async () => {
      const selectFields = ['id', ...visibleWhenFields].join(',');
      const { data } = await (supabaseClient as any)
        .from(`${tableName}`)
        .select(selectFields)
        .eq('id', recordId)
        .single();
      return data as Record<string, unknown> | null;
    },
    staleTime: 10_000,
    enabled: visibleWhenFields.length > 0 && !!recordId,
  });

  const handleClose = useCallback(() => setOpenDialog(null), []);

  const visibleActions = actions.filter((action) => {
    if (action.permission) {
      if (permissionsLoading) return false;
      if (permissions?.[action.key] !== true) return false;
    }
    if (action.visible_when && actionVisibility) {
      const fieldValue = String(actionVisibility[action.visible_when.field] ?? '');
      if (!action.visible_when.values.includes(fieldValue)) return false;
    }
    if (action.visible_when && !actionVisibility) return false;
    return true;
  });

  const showBadge = hasAchContext(actions, tableName);
  if (visibleActions.length === 0 && !showBadge) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
      {showBadge && <StripeModeBadge />}
      {visibleActions.length > 0 && (
        <div style={{ display: 'flex', gap: 4 }}>
          {visibleActions.map((action) => {
            const DialogComponent =
              action.config.dialog_component
                ? ACTION_DIALOG_REGISTRY[action.config.dialog_component]
                : null;

            return (
              <React.Fragment key={action.key}>
                <button
                  className="sky-btn sky-btn-primary"
                  onClick={() => {
                    if (action.action_type === 'edge_function_dialog' && DialogComponent) {
                      setOpenDialog(action.key);
                    } else if (action.action_type === 'navigate' && action.config.navigate_to) {
                      navigate(interpolatePath(action.config.navigate_to, record));
                    }
                  }}
                >
                  {action.label}
                </button>
                {DialogComponent && (
                  <DialogComponent
                    isOpen={openDialog === action.key}
                    onClose={handleClose}
                    prefill={interpolatePrefill(action.config.prefill, record)}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};
